import {
  createEffect,
  createSignal,
  onMount,
  Show,
  For,
  untrack,
  onCleanup,
  createUniqueId,
} from "solid-js";

import clsx from "~/util/html/style";
import type { Toast as ToastWithoutSource } from "~/api/page";
import { Button, IconButton } from "~/common/button";
import { lockScreenUp } from "~/common/session_lock";
import { router } from "~/inertia";
import { fetchAndProcessTurboStream } from "~/request";

type ToastSource = "turbo" | "inertia" | "js";
export type Toast = ToastWithoutSource & { source: ToastSource };

const ADD_TOASTS_EVENT = "toast-manager:add-toasts";

const DEFAULT_TOAST_DURATION_MS = 8000;

export function dispatchAddToasts(toasts: ToastWithoutSource[], source: ToastSource) {
  document.dispatchEvent(new CustomEvent(ADD_TOASTS_EVENT, { detail: { toasts, source } }));
}

export function addToast(toast: Pick<Toast, "button" | "text" | "type" | "durationMs">) {
  const newToast = { ...toast, id: createUniqueId() };
  dispatchAddToasts([newToast], "js");
}

export function ToastManager(props: { toasts: Toast[] }) {
  const [toasts, setToasts] = createSignal<Toast[]>([]);
  let recentToasts: Toast[] = [];
  // Not while the session is locked: the stack paints above the lock screen, and a toast can
  // name a patient. Held rather than dropped, so they show again if the lock lifts in place.
  const showing = () => toasts().length > 0 && !lockScreenUp();

  // dedupes toasts in case they are added to props multiple times somehow, like via back button
  function addToasts(toastsToAdd: Toast[]) {
    const newToasts = toastsToAdd.filter(
      (toast) =>
        !untrack(() => toasts()).some((t) => t.id === toast.id) &&
        !recentToasts.some((t) => t.id === toast.id),
    );
    if (newToasts.length === 0) return;

    recentToasts = [...newToasts, ...recentToasts].slice(0, 10);
    setToasts((toasts) => [...toasts, ...newToasts]);
  }

  function removeToast(id: string) {
    setToasts((toasts) => toasts.filter((toast) => toast.id !== id));
  }

  createEffect(() => {
    addToasts(props.toasts);
  });

  function handleAddToasts(event: Event) {
    const { toasts, source } = (
      event as CustomEvent<{ toasts: ToastWithoutSource[]; source: ToastSource }>
    ).detail;
    addToasts(toasts.map((toast) => ({ ...toast, source })));
  }

  onMount(() => {
    document.addEventListener(ADD_TOASTS_EVENT, handleAddToasts);
  });

  onCleanup(() => {
    document.removeEventListener(ADD_TOASTS_EVENT, handleAddToasts);
  });

  return (
    <Show when={showing()}>
      <div
        class="pointer-events-none fixed right-0 bottom-0 left-0 z-toast flex flex-col items-end justify-center gap-4 px-4 pb-2 not-in-has-data-chat-open:bottom-12 in-has-data-chat-open:right-(--chat-width) print:hidden"
        data-turbo-permanent
      >
        <For each={toasts()}>
          {(toast) => <Toast toast={toast} removeToast={removeToast} button={toast.button} />}
        </For>
      </div>
    </Show>
  );
}

function Toast(props: {
  toast: Toast;
  button?: { text: string; url: string };
  removeToast: (id: string) => void;
}) {
  const [visible, setVisible] = createSignal(true);

  function remove() {
    setVisible(false);
    setTimeout(() => props.removeToast(props.toast.id), 300);
  }

  onMount(() => {
    if (props.toast.durationMs === null) return;
    const expiry = setTimeout(remove, props.toast.durationMs ?? DEFAULT_TOAST_DURATION_MS);
    // A toast taken off the screen while the session is locked stops its clock, and gets its
    // full time when it is shown again, rather than running out where nobody can see it.
    onCleanup(() => clearTimeout(expiry));
  });

  return (
    <div
      id={props.toast.id}
      // How an open dialog tells a press on a toast from one on its backdrop (DialogContainer).
      data-toast
      class={clsx(
        "pointer-events-auto flex items-center gap-5 rounded-lg px-5 py-3 shadow-md transition-opacity duration-300 starting:opacity-0",
        props.toast.type === "success" && "bg-brand-success-light",
        props.toast.type === "error" && "bg-brand-danger-light",
        props.toast.type === "warning" && "bg-brand-warning-light",
        !visible() && "opacity-0",
      )}
    >
      <p class="text-sm font-medium text-typography-primary">{props.toast.text}</p>

      <Show when={props.toast.button}>
        {(button) => (
          <Button
            variant="plain"
            onClick={() => {
              if (button().navigate) {
                router.visit(button().url);
              } else if (props.toast.source === "inertia") {
                router.post(button().url);
              } else {
                const { url, method, onSuccess } = button();
                fetchAndProcessTurboStream(url, { method: method || "POST" }).then(() => {
                  onSuccess?.();
                });
              }
              remove();
            }}
          >
            {button().text}
          </Button>
        )}
      </Show>

      <IconButton variant="plain" iconName="x" onClick={remove} />
    </div>
  );
}
